<template>
  <div class="map">
    <SidePanel ref="sidePanel" v-if="showSidePanel" :map="map" />
    <LockdownDetailsEditor ref="lockdownDetailsEditor" />
    <Patient ref="patient" />
    <v-card style="visibility:hidden" id="legend">
      <v-subheader>{{ $t('Legend') }}</v-subheader>
      <v-card-text class="pt-0 pb-0 pr-1">
        <ul>
          <li v-for="(status, index) in translatedStatuses" :key="index">
            <div v-if="status.showOnMap" :class="['sphere ' + status.color]" />
            <label v-if="status.showOnMap">{{ status.label }}</label>
          </li>
          <li>
            <div class="blue-circle" />
            <label>{{ $t('in home quarantine') }}</label>
          </li>
        </ul>
      </v-card-text>
    </v-card>
    <div id="map"></div>
  </div>
</template>

<style>
@import '../../node_modules/mapbox-gl/dist/mapbox-gl.css';
@import '../../node_modules/mapbox-gl-draw/dist/mapbox-gl-draw.css';

.mapboxgl-popup-close-button {
  font-size: large;
  top: 5px;
  right: 5px;
}
.sphere {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  text-align: center;
  vertical-align: middle;
  font-size: 500%;
  position: relative;
  display: inline-block;
  margin: 5%;
}
.sphere::after {
  background-color: rgba(255, 255, 255, 0.3);
  content: '';
  height: 45%;
  width: 12%;
  position: absolute;
  top: 4%;
  left: 15%;
  border-radius: 50%;
  transform: rotate(40deg);
}
.blue-circle {
  height: 20px;
  width: 20px;
  background-color: white;
  border-radius: 50%;
  border: 1px solid;
  border-color: blue;
  text-align: center;
  vertical-align: middle;
  font-size: 500%;
  position: relative;
  display: inline-block;
  margin: 5%;
}
#legend {
  box-shadow: none;
  padding: 0 10px 10px 0;
  position: absolute;
  display: block;
  bottom: 10px;
  right: 10px;
  width: 180px;
  z-index: 2;
}
#legend ul {
  padding-left: 0;
  list-style-type: none;
}
#legend label {
  height: 15px;
  width: 20%;
  text-align: center;
  padding-left: 5px;
}
</style>

<style lang="sass">
@import "../helpers/map/AdminStatPopup.scss"
@import "../helpers/map/PatientPopup.scss"
@import "../helpers/map/ContactPlacePopup.scss"
</style>

<script>
import mapboxgl from 'mapbox-gl';
import MapboxDraw from 'mapbox-gl-draw/dist/mapbox-gl-draw.js';
import MapStyle from '../helpers/map/MapStyle';
import { authHeader } from '../helpers';
import ApiService from '../services/ApiService';
import SidePanel from '../components/SidePanel';
import { ScreenshotControl } from '../helpers/map/ScreenshotControl';
import { PatientPopup } from '../helpers/map/PatientPopup';
import { AdminStatPopup } from '../helpers/map/AdminStatPopup';
import { ContactPlaces } from '../helpers/map/ContactPlaces';
import { ContactLines } from '../helpers/map/ContactLines';
import { mapGetters, mapState, mapMutations } from 'vuex';
import Patient from '../components/Patient';
import LockdownDetailsEditor from '../components/LockdownDetailsEditor';
import { DATA_TRANSLATIONS } from '../helpers/dataTranslations';

let map;

const emptyFeatureCollection = {
  type: 'FeatureCollection',
  features: []
};

let selectedFeature = null;

const defaultLayers = {
  heatmap: ['heatmap', 'points'],
  cluster: []
};

const LOCKDOWN_LAYER_PREFIX = 'lockdown-';
const LINE_POSTFIX = '-line';
const CLUSTER_LAYER_PREFIX = 'cluster-';
const HEATMAP_LAYER_PREFIX = 'points-';
const CITY_POSTFIX = '-city';
const DISTRICT_POSTFIX = '-district';
const COUNTY_POSTFIX = '-county';

export default {
  name: 'Map',

  data() {
    return {
      draw: undefined,
      pointSource: {
        type: 'FeatureCollection',
        features: []
      },
      lockdownSource: {
        type: 'FeatureCollection',
        features: []
      },
      adminAreaSource: {
        type: 'FeatureCollection',
        features: []
      },
      contactLines: undefined,
      statuses: [],
      heatmapLayers: [],
      clusterLayers: [],
      lockdownLayers: [],
      showSidePanel: true,
      contactPlaces: undefined,
      isMapClickAvailable: true,
      map: map,
      sourceState: {
        heatmap: 0,
        cluster: 0,
        places: 0
      }
    };
  },
  computed: {
    ...mapGetters('account', ['isAdmin', 'isEditor']),
    ...mapState({
      mapCenter: (state) => state.map.center,
      mapZoom: (state) => state.map.zoom,
      mapMode: (state) => state.map.mode,
      mapData: (state) => {
        return {
          date: state.map.date,
          activeStatuses: state.map.activeStatuses,
          activePerson: state.map.activePerson,
          filterByPrimaryAddress: state.map.filterByPrimaryAddress,
          homeQuarantine: state.map.homeQuarantine,
          showLockdowns: state.map.showLockdowns,
          lastChangeOfRenderParams: state.map.lastChangeOfRenderParams
        };
      },
      config: (state) => state.app.config,
      translatedStatuses() {
        return this.statuses.map((x) => {
          return { ...x, label: this.$t(DATA_TRANSLATIONS[x.value]) };
        });
      }
    }),
    hasEditorRole() {
      return this.isAdmin || this.isEditor;
    },
    activePerson() {
      return this.mapData.activePerson;
    }
  },
  mounted() {
    if (this.config) {
      this.statuses = this.config.statuses;
      this.lockdownTypes = this.config.lockdownTypes;
      this.collectLayers();
    }
    let mapcatJson = MapStyle();
    map = new mapboxgl.Map({
      container: 'map',
      vector: true,
      zoom: this.mapZoom,
      minZoom: 6,
      maxZoom: 18,
      center: this.mapCenter,
      style: mapcatJson,
      hash: true,
      attributionControl: true,
      zoomControl: true,
      dragRotate: true,
      preserveDrawingBuffer: true,
      // using static locale, not integrated with language selector yet
      locale: {
        'NavigationControl.ZoomIn': 'Nagyítás',
        'NavigationControl.ZoomOut': 'Kicsinyítés',
        'NavigationControl.ResetBearing': 'Irányszög visszaállítása északra'
      },
      transformRequest: (url) => {
        return {
          url: url,
          headers: authHeader()
        };
      }
    });
    this.map = map;
    let zoomControl = new mapboxgl.NavigationControl();

    let scaleControl = new mapboxgl.ScaleControl({
      maxWidth: 100,
      unit: 'metric'
    });
    let screenshotControl = new ScreenshotControl({ withLegend: true });
    map.addControl(zoomControl);
    map.addControl(scaleControl, 'bottom-right');
    map.addControl(screenshotControl);
    var draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true
      }
    });
    if (this.hasEditorRole) {
      map.addControl(draw, 'top-right');
      this.draw = draw;
      map.on('draw.create', this.createPolygon);
      map.on('draw.update', this.updatePolygon);
      map.on('draw.delete', this.polygonDeleted);
      map.on('draw.modechange', this.drawModeChanged);
      map.on('click', this.selectPolygon);
    }
    map.on('moveend', () => {
      that.setCenter(map.getCenter());
    });
    map.on('zoomend', () => {
      that.setZoom(map.getZoom());
    });
    let that = this;
    map.on('load', function() {
      if (!map.getSource('lockdown')) {
        map.addSource('lockdown', {
          type: 'geojson',
          data: that.lockdownSource
        });
      }

      if (!map.getSource('adminArea')) {
        map.addSource('adminArea', {
          type: 'geojson',
          data: that.adminAreaSource
        });
      }
      if (!map.getSource('points')) {
        map.addSource('points', {
          type: 'geojson',
          data: that.pointSource
        });
      }

      for (let type of that.lockdownTypes) {
        if (!map.getLayer(LOCKDOWN_LAYER_PREFIX + type.value)) {
          map.addLayer({
            id: LOCKDOWN_LAYER_PREFIX + type.value,
            type: 'fill',
            source: 'lockdown',
            filter: ['==', 'type', type.value],
            paint: {
              'fill-color': type.color,
              'fill-opacity': 0.15
            }
          });
        }
        if (!map.getLayer(LOCKDOWN_LAYER_PREFIX + type.value + LINE_POSTFIX)) {
          map.addLayer({
            id: LOCKDOWN_LAYER_PREFIX + type.value + LINE_POSTFIX,
            type: 'line',
            source: 'lockdown',
            filter: ['==', 'type', type.value],
            paint: {
              'line-color': type.color,
              'line-width': 2
            }
          });
        }
      }

      if (!map.getLayer('points')) {
        map.addLayer({
          id: 'points',
          type: 'circle',
          source: 'points',
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 0, 10, 10],
            'circle-color': 'white',
            'circle-opacity': 0.0
          }
        });
      }

      for (let status of that.statuses) {
        if (status.showOnMap) {
          if (!map.getLayer(CLUSTER_LAYER_PREFIX + status.value + CITY_POSTFIX)) {
            map.addLayer(
              {
                id: CLUSTER_LAYER_PREFIX + status.value + CITY_POSTFIX,
                type: 'circle',
                source: 'adminArea',
                filter: ['all', ['==', 'type', 'city'], ['>', status.value, 0]],
                paint: {
                  'circle-color': status.color,
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['get', status.value],
                    1,
                    5,
                    500,
                    30
                  ],
                  'circle-stroke-color': 'white',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.6,
                  'circle-translate': status.circleTranslate
                },
                minzoom: 10
              },
              status.before ? CLUSTER_LAYER_PREFIX + status.before + CITY_POSTFIX : status.before
            );
          }

          if (!map.getLayer(CLUSTER_LAYER_PREFIX + status.value + DISTRICT_POSTFIX)) {
            map.addLayer(
              {
                id: CLUSTER_LAYER_PREFIX + status.value + DISTRICT_POSTFIX,
                type: 'circle',
                source: 'adminArea',
                filter: ['all', ['==', 'type', 'district'], ['>', status.value, 0]],
                paint: {
                  'circle-color': status.color,
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['get', status.value],
                    1,
                    5,
                    500,
                    30
                  ],
                  'circle-stroke-color': 'white',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.6,
                  'circle-translate': status.circleTranslate
                },
                minzoom: 8,
                maxzoom: 10
              },
              status.before
                ? CLUSTER_LAYER_PREFIX + status.before + DISTRICT_POSTFIX
                : status.before
            );
          }

          if (!map.getLayer(CLUSTER_LAYER_PREFIX + status.value + COUNTY_POSTFIX)) {
            map.addLayer(
              {
                id: CLUSTER_LAYER_PREFIX + status.value + COUNTY_POSTFIX,
                type: 'circle',
                source: 'adminArea',
                filter: ['all', ['==', 'type', 'county'], ['>', status.value, 0]],
                paint: {
                  'circle-color': status.color,
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['get', status.value],
                    1,
                    5,
                    500,
                    30
                  ],
                  'circle-stroke-color': 'white',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.6,
                  'circle-translate': status.circleTranslate
                },
                maxzoom: 8
              },
              status.before ? CLUSTER_LAYER_PREFIX + status.before + COUNTY_POSTFIX : status.before
            );
          }
          if (!map.getLayer(HEATMAP_LAYER_PREFIX + status.value)) {
            map.addLayer(
              {
                id: HEATMAP_LAYER_PREFIX + status.value,
                type: 'circle',
                source: 'points',
                filter: ['==', 'status', status.value],
                paint: {
                  'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 0, 10, 10],
                  'circle-color': status.color,
                  'circle-stroke-color': 'white',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.6,
                  'circle-translate': status.circleTranslate
                }
              },
              status.before ? HEATMAP_LAYER_PREFIX + status.before : status.before
            );
          }
        }
      }

      if (!map.getLayer('heatmap')) {
        map.addLayer(
          {
            id: 'heatmap',
            type: 'heatmap',
            source: 'points',
            maxzoom: 10,
            paint: {
              // // Increase the heatmap weight based on frequency and property magnitude
              'heatmap-weight': ['get', 'point_count'],
              // Increase the heatmap color weight weight by zoom level
              // // heatmap-intensity is a multiplier on top of heatmap-weight
              'heatmap-intensity': 2,
              // // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
              // // Begin color ramp at 0-stop with a 0-transparancy color
              // // to create a blur-like effect.
              'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                0,
                'rgba(33,102,172,0)',
                0.2,
                'rgb(103,169,207)',
                0.4,
                'rgb(209,229,240)',
                0.6,
                'rgb(253,219,199)',
                0.8,
                'rgb(239,138,98)',
                1,
                'rgb(178,24,43)'
              ],
              // Adjust the heatmap radius by zoom level
              'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 20],
              // Transition from heatmap to circle layer by zoom level
              'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 7, 1, 10, 0]
            }
          },
          'waterway-label'
        );
      }

      that.updateLockdowns();
      that.updateMode();
      that.setHomeQuarantineStyle();

      map.on('click', 'points', async (e) => {
        e.originalEvent.cancelBubble = true;
        if (e.features.length > 0) {
          let patientPopup = new PatientPopup(
            async (personId) => {
              let person = await ApiService.getPerson(personId);
              if (person.data) {
                that.$store.commit('map/setActivePerson', person.data);
              }
            },
            async (personId) => {
              let person = await ApiService.getPerson(personId);
              if (person.data) {
                that.$refs.patient.refresh(person.data, false, true);
              }
            },
            async (personId) => {
              let person = await ApiService.getPerson(personId);
              if (person.data) {
                that.$refs.patient.refresh(person.data, true, false);
              }
            },
            that.hasEditorRole
          );
          let ids = e.features.map((f) => f.properties.id);
          let coordinate = e.features[0].geometry.coordinates;
          let people = await ApiService.getPeopleByIds(ids);
          await that.contactLines.setData(people.data, that.pointSource);
          patientPopup.open(map, coordinate, people.data);
          patientPopup.on('close', () => {
            that.contactLines.clearData();
          });
        }
      });
      map.on('click', that.deselectPerson);
      map.on('mousemove', 'points', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'points', () => {
        map.getCanvas().style.cursor = '';
      });
      map.on('contextmenu', that.getAdminStat);
      that.updatePoints();
      that.updateAdminAreas();
    });
    this.initContactPlaces();
    this.initContactLines();
  },
  methods: {
    ...mapMutations('map', ['setZoom', 'setCenter']),
    drawModeChanged(e) {
      if (e.mode === 'draw_polygon') {
        this.isMapClickAvailable = false;
      }
    },
    async selectPolygon(e) {
      if (this.isMapClickAvailable) {
        let features = map.queryRenderedFeatures(e.point, {
          layers: this.lockdownLayers
        });
        if (features.length > 0) {
          e.originalEvent.cancelBubble = true;
          if (!selectedFeature || selectedFeature.properties.id !== features[0].properties.id) {
            this.draw.deleteAll();
            selectedFeature = features[0];
            let id = this.draw.add(selectedFeature);
            this.draw.changeMode('simple_select', { featureId: id[0] });
          }
        } else if (this.draw.getMode() === 'simple_select') {
          let features = this.draw.getSelected().features;
          selectedFeature = features ? features[0] : undefined;
        }
        if (selectedFeature !== null) {
          this.$refs.lockdownDetailsEditor.refresh(selectedFeature);
          if (selectedFeature === undefined) {
            this.showSidePanel = true;
            this.$refs.lockdownDetailsEditor.hide();
          } else {
            this.showSidePanel = false;
            this.$refs.lockdownDetailsEditor.show();
          }
        }
      }
    },
    clearSelectedPoygon() {
      this.isMapClickAvailable = true;
      this.draw.deleteAll();
      selectedFeature = null;
      this.showSidePanel = true;
      this.$refs.lockdownDetailsEditor.hide();
    },
    createPolygon(data) {
      this.isMapClickAvailable = false;
      let feature = data.features[0];
      this.$refs.lockdownDetailsEditor.refresh(feature);
      this.showSidePanel = false;
      this.$refs.lockdownDetailsEditor.show();
    },
    updatePolygon(data) {
      let feature = data.features[0];
      this.$refs.lockdownDetailsEditor.refresh(feature);
      this.showSidePanel = false;
      this.$refs.lockdownDetailsEditor.show();
    },
    async polygonCreated(feature) {
      this.isMapClickAvailable = true;
      feature = (await ApiService.createLockdown(feature)).data;
      this.lockdownSource.features.push(feature);
      map.getSource('lockdown').setData(this.lockdownSource);
      this.draw.deleteAll();
      this.showSidePanel = true;
      this.$refs.lockdownDetailsEditor.hide();
    },
    async polygonUpdated(feature) {
      feature = (await ApiService.updateLockdown(feature.properties.id, feature)).data;
      for (let i in this.lockdownSource.features) {
        if (this.lockdownSource.features[i].properties.id === feature.properties.id) {
          this.lockdownSource.features.splice(i, 1);
          break;
        }
      }
      this.lockdownSource.features.push(feature);
      map.getSource('lockdown').setData(this.lockdownSource);
      this.draw.deleteAll();
      this.showSidePanel = true;
      this.$refs.lockdownDetailsEditor.hide();
    },
    async polygonDeleted(data) {
      this.showSidePanel = true;
      this.$refs.lockdownDetailsEditor.hide();
      let feature = data.features[0];
      if (feature.properties.id) {
        await ApiService.removeLockdown(feature.properties.id);
        for (let i in this.lockdownSource.features) {
          if (this.lockdownSource.features[i].properties.id === feature.properties.id) {
            this.lockdownSource.features.splice(i, 1);
            break;
          }
        }
        map.getSource('lockdown').setData(this.lockdownSource);
      }
      selectedFeature = undefined;
      this.$refs.lockdownDetailsEditor.refresh(selectedFeature);
      this.draw.deleteAll();
    },
    async updateLockdowns() {
      if (this.mapData.showLockdowns) {
        let fc = await ApiService.getLockdowns({
          date: this.mapData.date
        });
        this.lockdownSource = fc.data;
      } else {
        this.lockdownSource = emptyFeatureCollection;
      }
    },
    async updatePoints(forceUpdate) {
      forceUpdate = forceUpdate === true;
      if (
        forceUpdate ||
        (this.mapMode === 'heatmap' &&
          this.sourceState.heatmap < this.mapData.lastChangeOfRenderParams)
      ) {
        this.sourceState.heatmap = this.mapData.lastChangeOfRenderParams;
        let points = await ApiService.getHeatmap({
          date: this.mapData.date,
          status: this.mapData.activeStatuses,
          personId: this.mapData.activePerson ? this.mapData.activePerson._id : undefined,
          byPrimaryAddress: this.mapData.filterByPrimaryAddress,
          withFade: this.mapData.activePerson ? true : false
        });

        if (points) {
          this.pointSource = points.data;
        }
      }
    },
    async updatePlaces() {
      if (
        this.mapMode === 'places' &&
        this.contactPlaces !== undefined &&
        this.sourceState.places < this.mapData.lastChangeOfRenderParams
      ) {
        this.sourceState.places = this.mapData.lastChangeOfRenderParams;
        await this.contactPlaces.changeMapStatus({
          date: this.mapData.date,
          byPrimaryAddress: this.mapData.filterByPrimaryAddress,
          status: this.mapData.activeStatuses
        });
      }
    },
    async updateAdminAreas() {
      if (
        this.mapMode === 'cluster' &&
        this.sourceState.cluster < this.mapData.lastChangeOfRenderParams
      ) {
        this.sourceState.cluster = this.mapData.lastChangeOfRenderParams;
        let adminData = await ApiService.getAdminAreaMap({
          date: this.mapData.date,
          status: this.mapData.activeStatuses,
          byPrimaryAddress: this.mapData.filterByPrimaryAddress,
          adminTypes: ['city', 'district', 'county'],
          geomType: 'point'
        });
        if (adminData) {
          this.adminAreaSource = adminData.data;
        }
      }
    },
    async initContactPlaces() {
      if (this.statuses.length > 0 && this.map !== undefined && this.contactPlaces === undefined) {
        this.contactPlaces = new ContactPlaces(this.map, this.statuses, async (personId) => {
          let person = await ApiService.getPerson(personId);
          if (person.data) {
            this.$refs.patient.refresh(person.data, false, true);
          }
        });
        await this.contactPlaces.init();
        this.updatePlaces();
        if (this.mapMode == 'places') {
          this.contactPlaces.show();
        } else {
          this.contactPlaces.hide();
        }
      }
    },
    async initContactLines() {
      if (this.map !== undefined && this.contactLines === undefined) {
        this.contactLines = new ContactLines(this.map);
        await this.contactLines.init();
      }
    },
    async updateMode() {
      if (this.mapMode == 'heatmap') {
        await this.updatePoints();
        this.setLayersVisibility(this.heatmapLayers, 'visible');
        this.setLayersVisibility(this.clusterLayers, 'none');
        if (this.contactPlaces !== undefined) {
          this.contactPlaces.hide();
        }
      } else if (this.mapMode == 'cluster') {
        await this.updateAdminAreas();
        this.setLayersVisibility(this.heatmapLayers, 'none');
        this.setLayersVisibility(this.clusterLayers, 'visible');
        if (this.contactPlaces !== undefined) {
          this.contactPlaces.hide();
        }
      } else if (this.mapMode == 'places') {
        await this.updatePlaces();
        this.setLayersVisibility(this.heatmapLayers, 'none');
        this.setLayersVisibility(this.clusterLayers, 'none');
        if (this.contactPlaces !== undefined) {
          this.contactPlaces.show();
        }
      }
    },
    setLayersVisibility(layerGroup, visibility) {
      for (let layername of layerGroup) {
        map.setLayoutProperty(layername, 'visibility', visibility);
      }
    },
    collectLayers() {
      this.heatmapLayers = [...defaultLayers.heatmap];
      this.clusterLayers = [];
      this.lockdownLayers = [];
      for (let status of this.statuses) {
        if (status.showOnMap) {
          this.heatmapLayers.push(HEATMAP_LAYER_PREFIX + status.value);
          this.clusterLayers.push(CLUSTER_LAYER_PREFIX + status.value + CITY_POSTFIX);
          this.clusterLayers.push(CLUSTER_LAYER_PREFIX + status.value + DISTRICT_POSTFIX);
          this.clusterLayers.push(CLUSTER_LAYER_PREFIX + status.value + COUNTY_POSTFIX);
        }
      }
      for (let type of this.lockdownTypes) {
        this.lockdownLayers.push(LOCKDOWN_LAYER_PREFIX + type.value);
      }
    },
    async getAdminStat(e) {
      if (!e.originalEvent.cancelBubble && this.isMapClickAvailable) {
        let adminStatData = await ApiService.getAdminStat({
          point: e.lngLat,
          date: this.mapData.date,
          byPrimaryAddress: this.mapData.filterByPrimaryAddress
        });

        if (this.adminStatPopup) {
          this.adminStatPopup.remove();
        }

        this.adminStatPopup = new AdminStatPopup();
        this.adminStatPopup.open(map, adminStatData.data, e.lngLat);
      }
    },
    setHomeQuarantineStyle() {
      let circleStrokeColor = 'white';
      if (this.mapData.homeQuarantine) {
        circleStrokeColor = ['match', ['to-string', ['get', 'hq']], 'true', 'blue', 'white'];
      }
      for (let hml of this.heatmapLayers) {
        if (defaultLayers.heatmap.indexOf(hml) === -1 && map.getLayer(hml)) {
          map.setPaintProperty(hml, 'circle-stroke-color', circleStrokeColor);
        }
      }
    },
    async refresh() {
      await this.updatePoints();
      await this.updateAdminAreas();
    },
    async deselectPerson() {
      this.$store.commit('map/setActivePerson', undefined);
      await this.contactLines.clearData();
    }
  },
  watch: {
    pointSource() {
      let pointsSource = map.getSource('points');
      if (pointsSource !== undefined) {
        pointsSource.setData(this.pointSource);
      }
    },
    adminAreaSource() {
      let adminAreaSource = map.getSource('adminArea');
      if (adminAreaSource !== undefined) {
        adminAreaSource.setData(this.adminAreaSource);
      }
    },
    lockdownSource() {
      let lockdownSource = map.getSource('lockdown');
      if (lockdownSource !== undefined) {
        lockdownSource.setData(this.lockdownSource);
      }
    },
    mapMode() {
      this.updateMode();
    },
    async mapData() {
      if (this.mapData.activePerson) {
        await this.updatePoints(true);
        let res = this.pointSource.features.find((x) => {
          return x.properties.id === this.mapData.activePerson._id;
        });
        if (res) {
          map.panTo(res.geometry.coordinates);
        }
      } else {
        await this.updatePoints();
      }
      await this.updateLockdowns();
      await this.updateAdminAreas();
      await this.updatePlaces();
      this.setHomeQuarantineStyle();
    },
    async activePerson(value) {
      if (value) {
        await this.contactLines.setData([value], this.pointSource);
      } else {
        await this.contactLines.clearData();
      }
    },
    config(config) {
      if (config) {
        this.statuses = config.statuses;
        this.lockdownTypes = config.lockdownTypes;
        this.collectLayers();
        this.initContactPlaces();
        this.initContactLines();
      }
    }
  },
  getMap() {
    return map;
  },
  components: {
    SidePanel,
    LockdownDetailsEditor,
    Patient
  }
};
</script>
